import React, {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';

import { useIsDesktop } from '../../hooks/useIsDesktop';
import { Card } from '../Card';
import { IconFigure } from '../Icons';
// import { IconAdmin } from '../Icons/IconAdmin';
import { IconAdmin } from '../Icons/IconAdmin';
import { PaginatorAsync } from '../PaginatorAsync';

import { TourneyListItems } from './TourneyListItems';

import { useMinileagueData } from '@/hooks/useMinileagueData';
import { PropsWithClassName } from '@/types';
import { GenericItemTourneysData } from '@/types/tda';
import { EnumContextGDT, EnumContextTourneys } from '@/types/team';
import { cn } from '@/utils';

const HeaderBadge = ({
  className,
  icon,
  iconClassName,
  label,
}: {
  className?: string;
  icon: ReactNode;
  iconClassName?: string;
  label: string;
}) => (
  <div className={cn('flex lg:justify-end items-center', className)}>
    <figure className={cn('mr-1', iconClassName)}>{icon}</figure>
    <p className="text-xs lg:text-sm text-gdt-gray-3 font-semibold">{label}</p>
  </div>
);

interface Props extends PropsWithClassName, GenericItemTourneysData {
  context?: EnumContextTourneys;
  contextGDT?: EnumContextGDT;
  list: GenericItemTourneysData[];
  paginator?: boolean;
  classNameWrapperList?: string;
  classPaginator?: string;
  loadMore?: (pageNumber?: number) => void;
  hasMore?: boolean;
  totalCount?: number;
  classNames?: {
    secondaryText?: string;
    subtitle?: string;
    titleContainer?: string;
  };
  headerAction?: ReactNode;
}

const ITEM_PER_PAGE_DESKTOP = 5;

export const TourneyList = React.memo(
  ({
    title,
    className,
    list,
    action,
    handleSubmit,
    loadMore,
    variant,
    context,
    contextGDT,
    paginator,
    classNameWrapperList,
    classPaginator,
    hasMore,
    classNames = {},
    headerAction,
    withAvatar,
    totalCount,
  }: Props) => {
    const { isDesktop, isMobile } = useIsDesktop();
    const [currentPage, setCurrentPage] = useState(0);
    const { miniligueData, handleSetMiniligueData } = useMinileagueData();
    const itemsPerPage = isDesktop ? ITEM_PER_PAGE_DESKTOP : list.length;

    // Páginas ya disponibles localmente (según lo que se cargó del server).
    const loadedPages = Math.ceil(list.length / itemsPerPage);
    // Páginas totales a mostrar en el paginador: si el padre informa el total
    // real de resultados (`totalCount`), se usa ese valor para que aparezcan
    // todas las casillas desde la primera carga; si no, se cae al total ya
    // cargado (comportamiento previo).
    const totalPages = totalCount
      ? Math.ceil(totalCount / itemsPerPage)
      : loadedPages;

    const paginatedList = useMemo(() => {
      const startIndex = currentPage * itemsPerPage;
      const endIndex = startIndex + itemsPerPage;
      return list.slice(startIndex, endIndex);
    }, [list, currentPage, itemsPerPage]);

    useEffect(() => {
      setCurrentPage(0);
    }, []);

    // FIX si cambia la cantidad de items va decreciendo y se decrementa la pagina actual
    useEffect(() => {
      if (currentPage >= totalPages && totalPages > 0) {
        setCurrentPage(totalPages - 1);
      }
    }, [currentPage, totalPages]);

    // Cuando el paginador muestra páginas cuyos datos todavía no llegaron del
    // server (porque `totalCount` revela más páginas de las que hay cargadas
    // localmente, por ej. al hacer click directo en una casilla adelantada),
    // se van pidiendo de a una hasta cubrir la página actual o hasta que el
    // padre indique que no hay más resultados. Pedir solo cuando hace falta
    // evita re-pedir páginas ya cargadas (lo que hacía aparecer/desaparecer
    // páginas al navegar).
    useEffect(() => {
      if (currentPage >= loadedPages && hasMore && loadMore) {
        loadMore();
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPage, loadedPages, hasMore]);

    const handlePageChange = (selectedItem: { selected: number }) => {
      setCurrentPage(selectedItem.selected);
    };

    const Row = useCallback(
      ({ item, index }: { item: GenericItemTourneysData; index: number }) => {
        return (
          <TourneyListItems
            key={`${item.id}-${index}`}
            action={action || item.action}
            classNames={classNames}
            context={context}
            disabled={item.disabled}
            estadistica={item.estadistica}
            id={item?.id}
            image={item.image}
            isAdmin={item.isAdmin}
            isVip={item.isVip}
            profileName={item.profileName}
            profilePicUrl={item.profilePicUrl}
            secundaryText={item.secundaryText}
            status={item.status}
            subtitle={item.subtitle || ''}
            textCentered={item.textCentered}
            title={item.title}
            variant={variant}
            withAvatar={withAvatar}
            onClick={(params) => {
              if (item?.id && handleSubmit) {
                handleSubmit(params);
              }
              handleSetMiniligueData({
                ...miniligueData,
                miniLiga: {
                  ...miniligueData.miniLiga,
                  id: item.id,
                },
              });
            }}
          />
        );
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      []
    );

    const renderList = useMemo(() => {
      const shouldShowInfiniteScroll = paginator && isMobile && list.length > 5;
      const showPaginator = paginator && isDesktop && list.length > 5;

      if (shouldShowInfiniteScroll) {
        return (
          <InfiniteScroll
            className={'!h-[500px]'}
            dataLength={list.length}
            hasMore={Boolean(hasMore)}
            loader={<></>}
            next={() => loadMore && loadMore()}
          >
            {list.map((item: GenericItemTourneysData, index) => (
              <Row key={`${index}-${item.id}`} index={index} item={item} />
            ))}
          </InfiniteScroll>
        );
      }

      const selectList = showPaginator ? paginatedList : list;

      return selectList.map((item: GenericItemTourneysData, index) => (
        <Row key={`${index}-${item.id}`} index={index} item={item} />
      ));
    }, [
      Row,
      hasMore,
      isDesktop,
      isMobile,
      list,
      loadMore,
      paginatedList,
      paginator,
    ]);

    const isFriendTournament = contextGDT === EnumContextGDT.FriendTournament;
    const showHeaderAction = Boolean(headerAction) && list.length > 1;

    return (
      <div className={className}>
        <div
          className={cn(
            'flex justify-between items-center',
            isFriendTournament ? 'lg:pr-4 pr-1 mb-3' : 'pr-4',
            classNames.titleContainer,
            {
              'flex items-start': isMobile,
            }
          )}
        >
          <p
            className={cn(
              'text-sm font-bold text-gdt-gray-2',
              isFriendTournament ? 'flex-1' : 'mb-3'
            )}
          >
            {title}
          </p>

          {isFriendTournament && (
            <div
              className={cn('flex', {
                'flex-col-reverse flex-none': isMobile,
              })}
            >
              <HeaderBadge
                className="lg:mr-2"
                icon={<IconAdmin />}
                iconClassName="w-6"
                label="Sos administrador"
              />
              <HeaderBadge
                icon={<IconFigure />}
                iconClassName="w-5"
                label="Torneo VIP"
              />
            </div>
          )}

          {!isFriendTournament && showHeaderAction && headerAction}
        </div>
        {isFriendTournament && showHeaderAction && headerAction}
        <Card
          className={cn(
            'space-y-3 p-0 w-full overflow-hidden bg-transparent',
            classNameWrapperList,
            {
              'bg-gdt-gray-7 !space-y-[1px]':
                context &&
                [
                  EnumContextTourneys.Sidebar,
                  EnumContextTourneys.Admin,
                  EnumContextTourneys.MainContentHome,
                ].includes(context) &&
                isDesktop,
            }
          )}
        >
          {renderList}

          {isDesktop && paginator && totalPages > 1 && (
            <PaginatorAsync
              key={currentPage}
              className={classPaginator}
              currentPage={currentPage}
              pageCount={totalPages}
              onPageChange={handlePageChange}
            />
          )}
        </Card>
      </div>
    );
  }
);

TourneyList.displayName = 'TourneyList';
